// ─── A WARM HOST'S CACHE (2026-10-21) ────────────────────────────────────────
// The quick 3D page (pages/internal/QuickRenderProbe.tsx) stays open on the
// VPS runner between jobs. It installs this cache in pdfGenerator so the décor
// pieces, cover art and photos of an album it drew a minute ago are not
// downloaded and re-scaled again. The SAME bytes come out either way (a file is
// keyed by its storage path, never by the signing token) — the drawing is
// untouched. No print page ever installs it: every print render is a fresh
// browser. Kept out of pdfGenerator so it can be tested without the generator.
export interface RenderBlobCache {
  get(key: string): Blob | undefined;
  set(key: string, blob: Blob): void;
  getNorm?(key: string): { bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' } | undefined;
  setNorm?(key: string, v: { bytes: Uint8Array; mime: 'image/jpeg' | 'image/png' }): void;
}

/** The file a URL names — a signed URL's token changes on every signing. Pure. */
export function renderCacheKey(url: string): string {
  return /\/storage\/v1\/object\/sign\//.test(url) ? url.split('?')[0] : url;
}

/** A byte-capped, time-capped LRU. */
export function makeByteLru<V>(capBytes: number, sizeOf: (v: V) => number, ttlMs = 30 * 60_000, now: () => number = Date.now) {
  const m = new Map<string, { v: V; size: number; at: number }>();
  let total = 0;
  const drop = (k: string) => { const e = m.get(k); if (e) { total -= e.size; m.delete(k); } };
  return {
    get(k: string): V | undefined {
      const e = m.get(k);
      if (!e) return undefined;
      if (now() - e.at > ttlMs) { drop(k); return undefined; }
      m.delete(k); m.set(k, e);           // most recent last
      return e.v;
    },
    set(k: string, v: V) {
      const size = Math.max(1, sizeOf(v));
      if (size > capBytes) return;
      drop(k);
      m.set(k, { v, size, at: now() });
      total += size;
      for (const key of m.keys()) { if (total <= capBytes) break; drop(key); }
    },
    get bytes() { return total; },
    get size() { return m.size; },
  };
}
